<template>
    <input
        name="google-autocomplete"
        ref="autocomplete"
        type="text"
        :class="classname"
        :id="id"
        :placeholder="placeholder"
        v-model="autocompleteText"
        @focus="onFocus()"
        @blur="onBlur()"
        @change="onChange"
        @keypress="onKeyPress"
        @keyup="onKeyUp"
    />
</template>

<script>
    import { AssetService } from "@/services/asset.service";
    import { JsonDenormalizer } from "@/helpers/JsonDenormalizer";

    const ADDRESS_COMPONENTS = {
        street_number: 'short_name',
        route: 'long_name',
        locality: 'long_name',
        administrative_area_level_1: 'long_name',
        administrative_area_level_2: 'long_name',
        country: 'long_name',
        postal_code: 'short_name',
    };

    const CITIES_TYPE = ['locality', 'administrative_area_level_3'];
    const REGIONS_TYPE = ['locality', 'sublocality', 'postal_code', 'country',
        'administrative_area_level_1', 'administrative_area_level_2'];
    const ADDRESS_TYPE = ['street_address', 'street_number','route', 'locality', 'sublocality', 'political',  'postal_code', 'country',
        'administrative_area_level_1', 'administrative_area_level_2'];

    export default {
        name: 'VueGoogleAutocomplete',

        props: {
          id: {
            type: String,
            required: true
          },

          classname: String,

          placeholder: {
            type: String,
            default: 'Start typing'
          },

          types: {
            type: String,
            default: 'address'
          },

          country: {
            type: [String, Array],
            default: null
          },

          enableGeolocation: {
            type: Boolean,
            default: false
          },

          geolocationOptions: {
            type: Object,
            default: null
          },
          address: {}
        },

        data() {
            return {
                /**
                 * The Autocomplete object.
                 *
                 * @type {Autocomplete}
                 * @link https://developers.google.com/maps/documentation/javascript/reference#Autocomplete
                 */
                autocomplete: null,

                /**
                 * Autocomplete input text
                 * @type {String}
                 */
                autocompleteText: '',

                geolocation: {
                    /**
                     * Google Geocoder Objet
                     * @type {Geocoder}
                     * @link https://developers.google.com/maps/documentation/javascript/reference#Geocoder
                     */
                    geocoder: null,

                    /**
                     * Filled after geolocate result
                     * @type {Coordinates}
                     * @link https://developer.mozilla.org/en-US/docs/Web/API/Coordinates
                     */
                    loc: null,

                    /**
                     * Filled after geolocate result
                     * @type {Position}
                     * @link https://developer.mozilla.org/en-US/docs/Web/API/Position
                     */
                    position: null
                }
            }
        },

        watch: {
            autocompleteText: function (newVal, oldVal) {
	            this.$emit('inputChange', { newVal, oldVal }, this.id);
            },
            country: function(newVal, oldVal) {
              this.autocomplete.setComponentRestrictions({
                country: this.country === null ? [] : this.country
              });
            }
        },

        mounted: function() {
          const options = {};

          if (this.types) {
            options.types = [this.types];
          }

          if (this.country) {
            options.componentRestrictions = {
              country: this.country
            };
          }

          this.autocomplete = new google.maps.places.Autocomplete(
                document.getElementById(this.id),
                options
            );

          this.autocomplete.addListener('place_changed', this.onPlaceChanged);


          this.$nextTick(() => {
            if (this.address) {
                this.autocompleteText = this.address.formatted_address;
                this.$nextTick(() => {
                    this.onPlaceChanged(this.address);
                    // this.focus()
                })
            }
          });
        },

        beforeDestroy() {
            // Clear all listeners involved with autocomplete input.
            // More details: https://stackoverflow.com/a/32663181
            const input = this.$refs.autocomplete;
            if (input && window.google && window.google.maps) {
                google.maps.event.clearInstanceListeners(input);
            }
            if (this.autocomplete) {
                this.autocomplete.unbindAll();
            }
            // For now, we just remove all .pac-containers which assumes
            // we'll never have more than one autocomplete mounted at
            // any given time. @todo - assign a random token to the
            // .pac-container on mount (should be last one in DOM)
            // and use that token to target & remove on unmount.
            window.console && console.log('Removing *all* Google Places autocomplete div.pac-container items.');
            document.querySelectorAll('.pac-container').forEach(function(el){
                el.remove();
            });
        },

        methods: {
            /**
             * When a place changed
             */
            onPlaceChanged(data = false) {

                let place = '';
                if (data) {
                    place = data;
                } else {
                    place = this.autocomplete.getPlace();
                }


                if (!place.geometry) {
                  // User entered the name of a Place that was not suggested and
                  // pressed the Enter key, or the Place Details request failed.
                  this.$emit('no-results-found', place, this.id);
                  return;
                }

                if (place.address_components !== undefined) {
                    // return returnData object and PlaceResult object
                    this.$emit('placechanged', this.formatResult(place), place, this.id);
                    this.$emit('locationSelect', place);

                    // update autocompleteText then emit change event
                    this.autocompleteText = document.getElementById(this.id).value
                    this.onChange()
                }
            },

            /**
             * When the input gets focus
             */
            onFocus() {
              this.biasAutocompleteLocation();
              this.$emit('focus');
            },

            /**
             * When the input loses focus
             */
            onBlur() {
              this.$emit('blur');
            },

            /**
             * When the input got changed
             */
            onChange() {
              this.$emit('change', this.autocompleteText);
            },

            /**
             * When a key gets pressed
             * @param  {Event} event A keypress event
             */
            onKeyPress(event) {
              this.$emit('keypress', event);
            },

            /**
             * When a keyup occurs
             * @param  {Event} event A keyup event
             */
            onKeyUp(event) {
              document.querySelectorAll(".pac-custom").forEach((pac_custom) => pac_custom.remove());
              this.fetchCustomPlaces(event.target.value);
              this.$emit('keyup', event);
            },

            fetchCustomPlaces: _.debounce(function (search) {
              // Remove custom place options
              document.querySelectorAll(".pac-custom").forEach((pac_custom) => pac_custom.remove());
              setTimeout(async () => {
                if (search) {
                  // Fetch and append custom place options
                  let customPlaces = await AssetService.all({ asset_name: search, code_type: "SITE" })
                    .then((response) => JsonDenormalizer.denormalize(response))
                    .then((assets) => {
                      console.log(assets);
                      return assets.map(asset => ({
                        name: asset.assetName,
                        latitude: asset.assetNodes.length ? asset.assetNodes[0].latitude : null,
                        longitude: asset.assetNodes.length ? asset.assetNodes[0].longitude : null,
                      }));
                    });

                  // If there are custom place options, display .pac-container
                  if (customPlaces.length) document.querySelector(".pac-container").style.display = "block"
                  // Display custom places
                  for (const customPlace of customPlaces) {
                    const pac_item = document.createElement("div");
                    pac_item.setAttribute("class", "pac-item pac-custom");
                    pac_item.setAttribute("data-lat", customPlace.latitude);
                    pac_item.setAttribute("data-lng", customPlace.longitude);
                    document.querySelector(".pac-container").appendChild(pac_item);
                    pac_item.addEventListener("mousedown", (e) => {
                      // Override default click event
                      this.$emit("placechanged", {
                        latitude: e.currentTarget.dataset.lat,
                        longitude: e.currentTarget.dataset.lng,
                      });
                      this.$refs.autocomplete.value = e.currentTarget.innerText;
                    });
                    const pac_icon = document.createElement("span");
                    pac_icon.setAttribute("class", "pac-icon pac-icon-marker");
                    pac_item.appendChild(pac_icon);
                    const pac_place = document.createElement("span");
                    pac_place.innerHTML = customPlace.name;
                    pac_item.appendChild(pac_place);
                  }
                }
              }, 500);
            }, 500),

            /**
             * Clear the input
             */
            clear() {
              this.autocompleteText = ''
            },

            /**
             * Focus the input
             */
            focus() {
              this.$refs.autocomplete.focus()
            },

            /**
             * Blur the input
             */
            blur() {
              this.$refs.autocomplete.blur()
            },

            /**
             * Update the value of the input
             * @param  {String} value
             */
            update (value) {
              this.autocompleteText = value
            },

            /**
             * Update the coordinates of the input
             * @param  {Coordinates} value
             */
            updateCoordinates (value) {


                if (!value && !(value.lat || value.lng)) return;
                if (!this.geolocation.geocoder) this.geolocation.geocoder = new google.maps.Geocoder();

                // const latlng = {
                //   lat: parseFloat(-36.84546743397956),
                //   lng: parseFloat(174.76159751415256),
                // };
                // console.log(latlng);
                // this.geolocation.geocoder.geocode({'location': latlng}, (results, status) => {
                //   if (status === "OK") {
                //     console.log(status);
                //     console.log(results);
                //   }
                // });


                this.geolocation.geocoder.geocode({'location': value}, (results, status) => {
                    if (status === 'OK') {
                        results = this.filterGeocodeResultTypes(results);
                        if (results[0]) {
                            this.$emit('placechanged', this.formatResult(results[0]), results[0], this.id);
                            this.update(results[0].formatted_address);
                        } else {
                            this.$emit('error', 'no result for provided coordinates');
                        }
                    } else {
                        this.$emit('error', 'error getting address from coords');
                    }
                })
            },

            /**
             * Update location based on navigator geolocation
             */
            geolocate () {
                this.updateGeolocation ((geolocation, position) => {
                    this.updateCoordinates(geolocation)
                })
            },

            /**
             * Update internal location from navigator geolocation
             * @param  {Function} (geolocation, position)
             */
            updateGeolocation (callback = null) {
                if (navigator.geolocation) {
                    let options = {};
                    if(this.geolocationOptions) Object.assign(options, this.geolocationOptions);
                    navigator.geolocation.getCurrentPosition(position => {
                        let geolocation = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude
                        };
                        this.geolocation.loc = geolocation;
                        this.geolocation.position = position;

                        if (callback) callback(geolocation, position);
                    }, err => {
                        this.$emit('error', 'Cannot get Coordinates from navigator', err);
                    }, options);
                }
            },


            // Bias the autocomplete object to the user's geographical location,
            // as supplied by the browser's 'navigator.geolocation' object.
            biasAutocompleteLocation () {
                if (this.enableGeolocation) {
                    this.updateGeolocation((geolocation, position) => {
                        let circle = new google.maps.Circle({
                            center: geolocation,
                            radius: position.coords.accuracy
                        });
                        this.autocomplete.setBounds(circle.getBounds());
                    })
                }
            },

            /**
             * Format result from Geo google APIs
             * @param place
             * @returns {{formatted output}}
             */
            formatResult (place) {
                let returnData = {};
                for (let i = 0; i < place.address_components.length; i++) {
                    let addressType = place.address_components[i].types[0];
                    if (ADDRESS_COMPONENTS[addressType]) {
                        let val = place.address_components[i][ADDRESS_COMPONENTS[addressType]];
                        returnData[addressType] = val;
                    }
                }

                returnData['latitude'] = place.geometry.location.lat();
                returnData['longitude'] = place.geometry.location.lng();

                return returnData
            },

            /**
             * Extract configured types out of raw result as
             * Geocode API does not allow to do it
             * @param results
             * @returns {GeocoderResult}
             * @link https://developers.google.com/maps/documentation/javascript/reference#GeocoderResult
             */
            filterGeocodeResultTypes (results) {
                if (!results || !this.types) return results;
                let output = [];
                let types = [this.types];
                if (types.includes('(cities)')) types = types.concat(CITIES_TYPE);
                if (types.includes('(regions)')) types = types.concat(REGIONS_TYPE);
                if (types.includes('address')) types = types.concat(ADDRESS_TYPE);

                for (let r of results) {
                    for (let t of r.types) {
                        if (types.includes(t)) {
                            output.push(r);
                            break;
                        }
                    }
                }
                
                return output;
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/components/partials/VueGoogleAutocomplete.vue